import type { IRecentAddressSuggestion } from '@/common/interfaces/mostRecent.interface';

export interface IStoreRecentAddressFormSnapshot {
  shippingName?: string;
  attentionName?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: { label?: string; value?: string } | string;
  shippingZipCode?: string;
}

const toFormString = (value: unknown): string => (value == null ? '' : String(value));

const normalizeAddressField = (value: unknown): string => {
  let normalized = toFormString(value).trim().replace(/\s+/g, ' ');
  normalized = normalized.toLowerCase();
  return normalized;
};

const resolveStateValue = (state: IStoreRecentAddressFormSnapshot['state']): string => {
  if (typeof state === 'string') {
    return state;
  }

  return state?.value ?? '';
};

export const buildRecentAddressFormValues = (
  address: IRecentAddressSuggestion,
  currentAttentionName = ''
): IStoreRecentAddressFormSnapshot => ({
  shippingName: toFormString(address.shippingName),
  attentionName: toFormString(address.attentionName || currentAttentionName),
  street1: toFormString(address.street1),
  street2: toFormString(address.street2),
  city: toFormString(address.city),
  state: address.state ? { label: address.state, value: address.state } : { label: '', value: '' },
  shippingZipCode: toFormString(address.zipCode),
});

export const buildEmptyRecentAddressFormValues = (): IStoreRecentAddressFormSnapshot => ({
  shippingName: '',
  attentionName: '',
  street1: '',
  street2: '',
  city: '',
  state: { label: '', value: '' },
  shippingZipCode: '',
});

export const addressFieldsMatchRecentSuggestion = (
  formAddress: IStoreRecentAddressFormSnapshot,
  savedAddress: IRecentAddressSuggestion
): boolean => {
  const formComparable = {
    shippingName: formAddress.shippingName,
    street1: formAddress.street1,
    street2: formAddress.street2,
    city: formAddress.city,
    state: resolveStateValue(formAddress.state),
    zipCode: formAddress.shippingZipCode,
  };

  const keys = ['shippingName', 'street1', 'street2', 'city', 'state', 'zipCode'] as const;

  return keys.every((key) => {
    const formValue = key === 'zipCode' ? formComparable.zipCode : formComparable[key];
    const savedValue = savedAddress[key === 'zipCode' ? 'zipCode' : key] ?? '';

    return normalizeAddressField(formValue) === normalizeAddressField(savedValue);
  });
};

export const findMatchingRecentAddress = (
  formAddress: IStoreRecentAddressFormSnapshot,
  recentAddresses: IRecentAddressSuggestion[]
): IRecentAddressSuggestion | null =>
  recentAddresses.find((address) => addressFieldsMatchRecentSuggestion(formAddress, address)) ??
  null;

export const getRecentAddressAriaLabel = (address: IRecentAddressSuggestion): string =>
  `Select address: ${address.street1}, ${address.city}, ${address.state} ${address.zipCode}`;
